import { useState, useRef, RefObject } from "react";
import {
  Button,
  Modal,
  DateInput,
  TextInput,
  TextAreaInput,
  Grid,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";
import UserCocSelector from "@/components/Forms/components/Selectors/UserCocSelector";

import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";

import type {
  BaseCocModalProps,
  UserOption,
  CocSignatureObject,
} from "../Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";
import { createSignature } from "../utils";
import { SignatureField, SignatureFieldRef } from "@/Features";

interface RecollectCocModalProps extends BaseCocModalProps {}

interface RecollectFormData {
  received_by: UserOption | null;
  received_from: string;
  timestamp: string;
  reason: string;
}

const recollectValidationSchema = z.object({
  received_by: z
    .object(
      {
        user_id: z.number(),
        full_name: z.string(),
        location_id: z.number(),
      },
      {
        required_error: "Received by user is required",
      }
    )
    .refine((val) => val !== null && val !== undefined, {
      message: "Received by user is required",
    }),
  received_from: z
    .string({
      required_error: "Received from is required",
    })
    .min(1, { message: "Received from is required" }),
  timestamp: z
    .string({
      required_error: "Timestamp is required",
    })
    .min(1, { message: "Timestamp is required" }),
  reason: z.string().optional(),
});

const RecollectCocModal: React.FC<RecollectCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<RecollectFormData>({
    mode: "uncontrolled",
    initialValues: {
      received_by: null,
      received_from: "",
      timestamp: new Date().toISOString(),
      reason: "",
    },
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const receivedBySig = useRef<SignatureFieldRef>();
  const receivedFromSig = useRef<SignatureFieldRef>();

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(recollectValidationSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      return;
    }

    setSubmitting(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_to_sig: createSignature(
          receivedBySig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
        custody_from_sig: createSignature(
          receivedFromSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
      };

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
          case_number: defaultInfo.case_number,
          case_ref: defaultInfo.case_ref,
        },
        evidenceInfo: {
          evidence_id: defaultInfo.evidence_id,
          uuid: defaultInfo.uuid,
          evidence_number: defaultInfo.evidence_number,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id:
            defaultInfo.is_parent === 1
              ? formData.received_by?.location_id || 0
              : 0,
          new_location_name: formData.received_by?.full_name || "",
          current_location_name: formData.received_from,
          reason: formData.reason || "Evidence Re-Collected",
          timestamp: db_timestamp(formData.timestamp, true),
          signature: JSON.stringify(signature),
          coc_type: "Re-Collect",
        },
      };

      await EvidenceCocAPI.moveEvidence(requestObj);

      enqueueSnackbar(
        `Evidence Item Re-collected from ${formData.received_from}`,
        {
          variant: "success",
        }
      );

      onSubmit?.(requestObj);
    } catch (error) {
      console.error("Error re-collecting evidence:", error);
      enqueueSnackbar(`Error re-collecting evidence item.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose?.();
  };

  return (
    <Modal open={show} onClose={onClose} style={{ width: 600 }}>
      <Modal.Title>Recollect Evidence Item</Modal.Title>
      <div style={{ margin: "20px 0px" }}>
        <Grid col={1}>
          <UserCocSelector
            label="Received By"
            required
            placeholder="Select the user receiving this evidence..."
            key={form.key("received_by")}
            {...form.getInputProps("received_by")}
          />
          <TextInput
            label="Received From"
            required
            placeholder="Enter name..."
            key={form.key("received_from")}
            {...form.getInputProps("received_from")}
          />
          <DateInput
            label="Timestamp"
            required
            format="YYYY-MM-DD HH:mm:ss"
            enableCalendar
            arrow
            key={form.key("timestamp")}
            {...form.getInputProps("timestamp")}
          />
          <TextAreaInput
            label="Re-Collection Notes"
            minRows={3}
            key={form.key("reason")}
            {...form.getInputProps("reason")}
          />
          <div>
            <SignatureField
              sigFieldRef={receivedBySig}
              label="Received By Signature"
              useESignature={true}
            />
          </div>
          <div>
            <SignatureField
              sigFieldRef={receivedFromSig}
              label="Received From Signature"
              useESignature={false}
            />
          </div>
        </Grid>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Recollect Evidence
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default RecollectCocModal;
